import { OverlayModule } from '@angular/cdk/overlay';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  ENVIRONMENT_INITIALIZER,
  inject,
  isDevMode,
  NgZone,
  provideZoneChangeDetection,
} from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter } from '@angular/router';

import { akitaConfig, akitaDevtools, DevtoolsOptions } from '@datorama/akita';
import { AbstractSecurityStorage, provideAuth, StsConfigHttpLoader, StsConfigLoader } from 'angular-auth-oidc-client';
import { map } from 'rxjs';

import { OidcSynchronizedStorageService } from '@auth/service/oidc-synchronized-storage.service';
import { TabSyncService } from '@auth/service/tab-sync.service';
import { authErrorInterceptor } from '@common/interceptor/auth-error.interceptor';
import { conditionalWriteInterceptor } from '@common/interceptor/conditional-write.interceptor';
import { outageInterceptor } from '@common/interceptor/outage.interceptor';
import { requestTransformerInterceptor } from '@common/interceptor/request-transformer.interceptor';
import { MARKPRESS_CONFIG } from '@common/markpress';
import { MarkdownPipe } from '@common/pipe/markdown.pipe';
import { AuthDataService } from '@common/service/auth-data.service';
import { ParserService } from '@common/service/parser.service';
import { DEFAULT_TOKEN_RENEW_TIME, OIDC_CONFIG } from '@common/util/constants';
import { registerParseHelper } from '@common/util/template.util';
import { WidgetIncludesService } from '@common/widget-includes/widget-includes.service';

import { routes } from './app.routes';

const oidcHttpLoaderFactory = (authDataService: AuthDataService) => {
  const config$ = authDataService.getAuthData().pipe(
    map((oidcConfig) => {
      return {
        ...OIDC_CONFIG,
        // eslint-disable-next-line
        renewTimeBeforeTokenExpiresInSeconds: 60 * (oidcConfig.token_renew_time || DEFAULT_TOKEN_RENEW_TIME), // defaults to 5 mins
        redirectUrl: oidcConfig.postauth_uri,
        authority: `https://cognito-idp.${oidcConfig.cognito_region}.amazonaws.com/${oidcConfig.user_pool_id}`,
        clientId: oidcConfig.client_id,
        customParamsEndSessionRequest: {
          client_id: oidcConfig.client_id,
        },
      };
    }),
  );

  return new StsConfigHttpLoader(config$);
};

// Enable global store reset functionality
akitaConfig({ resettable: true });

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    { provide: MARKPRESS_CONFIG, useValue: { theme: 'aiwb', palette: 'sand' } },
    MarkdownPipe,
    // provideClientHydration(withEventReplay()), // SSR
    provideAnimationsAsync(),
    provideHttpClient(
      withInterceptorsFromDi(),
      // conditionalWriteInterceptor LAST (AK-720): on the request leg it must see the URL after
      // requestTransformerInterceptor rewrites it to the absolute core endpoint; on the response
      // leg it must see 412/428 before authErrorInterceptor.
      // outageInterceptor innermost (AIWB-1175): it must see raw backend errors before conditionalWrite/authError,
      // and its replay re-issues only the backend call.
      withInterceptors([
        requestTransformerInterceptor,
        authErrorInterceptor,
        conditionalWriteInterceptor,
        outageInterceptor,
      ]),
    ),
    OverlayModule,
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: (iconRegistry: MatIconRegistry) => () => {
        iconRegistry.setDefaultFontSetClass('material-symbols-outlined');
      },
      deps: [MatIconRegistry],
    },
    {
      // Bridge Handlebars HTML templates to ParserService expressions via the `parse` helper:
      //   {{ parse "@uuid" }}, {{ parse "$context.user.user_id" }}
      // Registered once here because template.util.ts (where helpers live) has no Angular DI.
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: (parser: ParserService) => () => {
        registerParseHelper((expr, context) => parser.transform(expr, context));
      },
      deps: [ParserService],
    },
    {
      // WL-0443: start fetching the startup widget includes now (non-blocking; never delays boot).
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: (widgetIncludes: WidgetIncludesService) => () => {
        void widgetIncludes.start();
      },
      deps: [WidgetIncludesService],
    },
    provideAuth({
      loader: {
        provide: StsConfigLoader,
        useFactory: oidcHttpLoaderFactory,
        deps: [AuthDataService],
      },
    }),
    TabSyncService,
    { provide: AbstractSecurityStorage, useClass: OidcSynchronizedStorageService },
    ...(isDevMode() ? [provideAkitaDevtools()] : []),
  ],
};

export function provideAkitaDevtools(options: Partial<DevtoolsOptions> = {}) {
  return {
    provide: ENVIRONMENT_INITIALIZER,
    multi: true,
    useFactory() {
      return () => {
        akitaDevtools(inject(NgZone), options);
      };
    },
  };
}
